import { createSlice } from '@reduxjs/toolkit';

// ============================================================================
// CONSTANTS
// ============================================================================

export const TRANSCRIPTION_STATUS = {
  IDLE: 'idle',
  TRANSCRIBING: 'transcribing',
  POLLING: 'polling',
  COMPLETED: 'completed',
  ERROR: 'error',
};

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
  status: TRANSCRIPTION_STATUS.IDLE,

  // Batch info
  batchId: null,

  // Progress data from API
  progressData: null,

  // Selected service
  selectedService: null,

  // Error message
  error: null,
};

// ============================================================================
// SLICE
// ============================================================================

const transcriptionSlice = createSlice({
  name: 'transcription',
  initialState,
  reducers: {
    // Start transcription
    startTranscription(state, action) {
      state.status = TRANSCRIPTION_STATUS.TRANSCRIBING;
      state.selectedService = action.payload.service;
      state.error = null;
    },

    // Set batch ID and start polling
    startPolling(state, action) {
      state.status = TRANSCRIPTION_STATUS.POLLING;
      state.batchId = action.payload.batchId;
    },

    // Update progress data
    updateProgress(state, action) {
      state.progressData = action.payload;

      // Check if complete
      if (action.payload?.is_complete) {
        state.status = TRANSCRIPTION_STATUS.COMPLETED;
      }
    },

    // Mark as completed
    completeTranscription(state) {
      state.status = TRANSCRIPTION_STATUS.COMPLETED;
    },

    // Set error
    setError(state, action) {
      state.status = TRANSCRIPTION_STATUS.ERROR;
      state.error = action.payload;
    },

    // Clear/dismiss progress (user action)
    dismissProgress(state) {
      state.progressData = null;
      state.batchId = null;
      state.status = TRANSCRIPTION_STATUS.IDLE;
    },

    // Reset all state
    resetTranscription() {
      return initialState;
    },

    // Set selected service
    setSelectedService(state, action) {
      state.selectedService = action.payload;
    },
  },
});

// ============================================================================
// EXPORTS
// ============================================================================

export const {
  startTranscription,
  startPolling,
  updateProgress,
  completeTranscription,
  setError,
  dismissProgress,
  resetTranscription,
  setSelectedService,
} = transcriptionSlice.actions;

// ============================================================================
// SELECTORS
// ============================================================================

export const selectTranscriptionStatus = (state) => state.transcription.status;
export const selectBatchId = (state) => state.transcription.batchId;
export const selectProgressData = (state) => state.transcription.progressData;
export const selectSelectedService = (state) => state.transcription.selectedService;
export const selectTranscriptionError = (state) => state.transcription.error;

export const selectIsTranscribing = (state) =>
  state.transcription.status === TRANSCRIPTION_STATUS.TRANSCRIBING;

export const selectIsPolling = (state) =>
  state.transcription.status === TRANSCRIPTION_STATUS.POLLING;

export const selectShowProgress = (state) => {
  const { progressData, batchId, status } = state.transcription;
  return (
    progressData && !progressData.is_complete && batchId && status === TRANSCRIPTION_STATUS.POLLING
  );
};

export const selectOverallProgress = (state) =>
  state.transcription.progressData?.overall_progress_percentage || 0;

export const selectTranscriptionProgress = (state) =>
  state.transcription.progressData?.transcription;

export const selectAnalysisProgress = (state) => state.transcription.progressData?.analysis;

export const selectTotalFailed = (state) => {
  const transcription = state.transcription.progressData?.transcription;
  const analysis = state.transcription.progressData?.analysis;
  return (transcription?.failed || 0) + (analysis?.failed || 0);
};

export default transcriptionSlice.reducer;
